import { Review as CtReview } from '@danone-global/ct/interfaces'
import BrandLogo from '@danone-global/internal/react/components/brand-logo'
import FormattedHtmlMessage from '@danone-global/internal/react/components/formatted-html-message'
import {
  ThumbDownIcon,
  ThumbUpIcon,
  VerifiedIcon,
} from '@danone-global/internal/react/components/icons'
import Rating from '@danone-global/internal/react/components/rating'
import {
  Grid,
  IconButton,
  Typography,
} from '@danone-global/internal/react/components/ui'
import {
  Theme,
  useCore,
  useFromCustom,
} from '@danone-global/internal/react/core'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React from 'react'
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'

import { IReviewLayout, ReviewData } from '../../reviews.type'

export interface Props extends CtReview {
  productName: string
  upvote: (id: string) => void
  downVote: (id: string) => void
  sku: string
  provideStructuredData?: boolean
  stringComponentLayout?: string
  starClasses?: string
  starActiveClasses?: string
}

export const Review: React.FC<Props> = ({
  id,
  productName,
  title,
  text,
  custom,
  authorName,
  rating,
  createdAt,
  upvote,
  downVote,
  lastModifiedAt,
  sku,
  provideStructuredData,
  stringComponentLayout = JSON.stringify([
    ReviewData.RATING,
    ReviewData.TITLE,
    ReviewData.DATE_AUTHOR,
    ReviewData.TEXT,
    ReviewData.RESPONSE,
    ReviewData.VOTE,
  ]),
  starClasses = '',
  starActiveClasses = '',
}) => {
  let componentLayout: IReviewLayout
  try {
    const JSONComponentLayout = stringComponentLayout.replace(/'/g, '"')
    componentLayout = JSON.parse(JSONComponentLayout)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
  }
  const classes = useStyles()
  const { localeConfig } = useCore()
  const isVerified = useFromCustom(
    custom.customFieldsRaw,
    'isVerified',
    false,
    lastModifiedAt,
  )
  const helpfulAmount = useFromCustom(
    custom.customFieldsRaw,
    'helpfulAmount',
    0,
    lastModifiedAt,
  )
  const unhelpfulAmount = useFromCustom(
    custom.customFieldsRaw,
    'unhelpfulAmount',
    0,
    lastModifiedAt,
  )
  const anonymous = useFromCustom(custom.customFieldsRaw, 'anonymous', false)
  const childAge = useFromCustom(custom.customFieldsRaw, 'childAge', null)
  const careLineMember = useFromCustom(
    custom.customFieldsRaw,
    'carelineMember',
    null,
  )
  const commentText = useFromCustom(custom.customFieldsRaw, 'commentText', null)
  const careLineCommentTimestamp = useFromCustom(
    custom.customFieldsRaw,
    'carelineCommentTimestamp',
    null,
  )
  const showCommunicationOptions = useFromCustom(
    custom.customFieldsRaw,
    'showCommunicationOptions',
    null,
  )
  const intl = useIntl()

  const handleUpvote = React.useCallback(() => {
    upvote(id)
  }, [upvote, id])

  const handleDownVote = React.useCallback(() => {
    downVote(id)
  }, [downVote, id])

  const structuredData =
    provideStructuredData === true
      ? {
          '@context': 'https://schema.org/',
          '@type': 'Product',
          name: productName,
          review: {
            reviewRating: {
              '@type': 'Rating',
              ratingValue: rating,
            },
            reviewBody: text,
            name: title,
            author: {
              '@type': 'Person',
              name: authorName,
            },
          },
          author: {
            '@type': 'Person',
            name: authorName,
          },
        }
      : undefined

  const displayRating = (
    <Rating
      className={classes.rating}
      rating={rating}
      starActiveClasses={starActiveClasses}
      starClasses={starClasses}
      variant="small"
    />
  )

  const displayTitle = title ? (
    <Typography className={classes.title} variant="h3">
      {title}
    </Typography>
  ) : undefined

  const displayDateAuthor = (
    <Typography className={classes.meta}>
      <span>
        <FormattedDate value={createdAt} />
      </span>

      {childAge ? (
        <span>
          <FormattedMessage
            defaultMessage="Child age: {childAge}"
            id="widgets.reviews.review.child-age"
            values={{ childAge }}
          />
        </span>
      ) : undefined}

      <span className={classes.authorName}>
        {anonymous ? (
          <FormattedMessage
            defaultMessage="Anonymous"
            id="widgets.reviews.review.anonymous"
          />
        ) : (
          authorName
        )}

        {isVerified ? (
          <span className={classes.verified}>
            <VerifiedIcon height={15} width={15} />

            <FormattedMessage
              defaultMessage="verified buyer"
              id="widgets.reviews.review.verified"
            />
          </span>
        ) : undefined}
      </span>
    </Typography>
  )

  const displayText = (
    <Typography className={classes.content} noGutters>
      {text}
    </Typography>
  )

  const displayResponse =
    careLineMember && commentText ? (
      <div className={classes.careLineContent}>
        <BrandLogo className={classes.brandLogo} />

        <div className={classes.careLineBody}>
          <Typography className={classes.careLineSubject} variant="h4">
            <FormattedMessage
              defaultMessage="Thank you for your message"
              id="widgets.reviews.review.reply-thank-you"
            />
          </Typography>

          <Typography className={classes.careLineMeta}>
            <span>{careLineMember}</span>

            <span>
              <FormattedDate value={careLineCommentTimestamp} />
            </span>
          </Typography>

          <Typography className={classes.careLineComment}>
            {commentText}
          </Typography>

          {showCommunicationOptions ? (
            <Typography className={classes.careLineComment}>
              <FormattedHtmlMessage
                message={intl.formatMessage(
                  {
                    id: 'global.any-questions',
                    defaultMessage:
                      'For any questions about ordering online, you can read our FAQ \'<a\' href="{faqUrl}" target="_blank">here\'</a>\'. You can also \'<a\' href="{contactUrl}" target="_blank">contact\'</a>\' us if you have more questions.',
                  },
                  {
                    contactUrl: localeConfig.urls?.contact,
                    faqUrl: localeConfig.urls?.faq,
                  },
                )}
              />
            </Typography>
          ) : undefined}
        </div>
      </div>
    ) : undefined

  const displayVote = (
    <Grid className={classes.actions} container>
      <IconButton
        className={classes.action}
        data-testid={`btn.reviews.${id}.upvote`}
        onClick={handleUpvote}
        variant="brand"
      >
        <ThumbUpIcon size={20} />
        {helpfulAmount}
      </IconButton>

      <IconButton
        className={classes.action}
        data-testid={`btn.reviews.${id}.down-vote`}
        onClick={handleDownVote}
        variant="brand"
      >
        <ThumbDownIcon size={20} />
        {unhelpfulAmount}
      </IconButton>
    </Grid>
  )

  function renderComponent(component: ReviewData): JSX.Element {
    switch (component) {
      case ReviewData.RATING:
        return displayRating
      case ReviewData.TITLE:
        return displayTitle
      case ReviewData.DATE_AUTHOR:
        return displayDateAuthor
      case ReviewData.TEXT:
        return displayText
      case ReviewData.RESPONSE:
        return displayResponse
      case ReviewData.VOTE:
        return displayVote
      default: {
        // eslint-disable-next-line no-console
        console.warn(`${component} does not exist`)
      }
    }
  }

  function generateDOMForStructure(componentLayout: IReviewLayout) {
    return (
      <>
        {componentLayout.map((component, index) => {
          if (typeof component === 'string') {
            return (
              <React.Fragment key={index}>
                {renderComponent(component as ReviewData)}
              </React.Fragment>
            )
          } else if (Array.isArray(component)) {
            return <div key={index}>{generateDOMForStructure(component)}</div>
          }
          return null
        })}
      </>
    )
  }

  return (
    <div
      className={clsx('dtc-review', classes.root)}
      data-testid={`${sku}-review`}
      id={id}
      style={{ whiteSpace: 'break-spaces' }}
    >
      {structuredData ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      ) : undefined}
      {generateDOMForStructure(componentLayout)}
    </div>
  )
}

export const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      border: '1px solid #DDDDDD',
      borderRadius: theme.shape.borderRadius(),
      padding: theme.spacing(4),
      boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',

      '&:not(:last-child)': {
        marginBottom: theme.spacing(2),
      },

      [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(8),

        '&:not(:last-child)': {
          marginBottom: theme.spacing(4),
        },
      },
    },

    info: {
      paddingLeft: theme.spacing(2),
      marginBottom: theme.spacing(2),

      [theme.breakpoints.up('md')]: {
        '& div:last-of-type': {
          display: 'flex',
          justifyContent: 'flex-end',
        },
      },
    },

    rating: {
      marginBottom: theme.spacing(1),
    },

    title: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      margin: theme.spacing(2, 0, 0),
    },

    authorName: {
      display: 'inline-flex',
      alignItems: 'center',
    },

    verified: {
      alignItems: 'center',
      display: 'inline-flex',
      verticalAlign: 'top',
      color: '#4da642',
      marginLeft: theme.spacing(1),

      '& svg': {
        marginRight: theme.spacing(0.5),
      },
    },

    meta: {
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: 'column',
      fontSize: 14,
      lineHeight: 1.6,
      marginBottom: theme.spacing(3),

      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',

        '& > span:not(:last-child)::after': {
          content: '"|"',
          display: 'inline-block',
          marginLeft: theme.spacing(1.5),
          marginRight: theme.spacing(1.5),
        },
      },
    },

    content: {
      borderRadius: theme.shape.borderRadius(),
      backgroundColor: theme.palette.common.white,
      marginBottom: theme.spacing(4),
      wordBreak: 'break-word',
    },

    careLineComment: {
      wordBreak: 'break-word',

      '& a': {
        color: 'inherit',
        textDecoration: 'underline',
      },
      '& a:hover': {
        textDecoration: 'none',
      },
    },

    careLineContent: {
      borderRadius: theme.shape.borderRadius(),
      backgroundColor: theme.utils.hexToRgba(theme.palette.primary.main, 0.1),
      flexWrap: 'nowrap',
      padding: theme.spacing(4),
      paddingLeft: theme.spacing(4),
      paddingBottom: theme.spacing(8),

      margin: theme.spacing(2, -2, 4),

      [theme.breakpoints.up('lg')]: {
        marginLeft: 0,
        marginRight: 0,
        marginTop: theme.spacing(8),
        paddingTop: theme.spacing(4),
        paddingRight: theme.spacing(3),
        display: 'flex',
        alignItems: 'flex-start',
      },
    },

    careLineBody: {},

    careLineMeta: {
      display: 'flex',
      fontSize: 14,
      lineHeight: 1.4,
      marginTop: 0,
      marginBottom: theme.spacing(3),
      flexDirection: 'row',

      '& > span:not(:last-child)::after': {
        content: '"|"',
        display: 'inline-block',
        marginLeft: theme.spacing(1.5),
        marginRight: theme.spacing(1.5),
      },
    },

    brandLogo: {
      display: 'flex',
      aspectRatio: '1/1',
      alignItems: 'center',
      maxWidth: '5em',
      border: '1px solid #DDDDDD',
      backgroundColor: '#fff',
      borderRadius: 4,
      padding: theme.spacing(0.5),
      marginBottom: theme.spacing(2),

      '& img': {
        width: '100%',
      },

      [theme.breakpoints.up('lg')]: {
        flex: '0 0 auto',
        marginRight: theme.spacing(4),
      },
    },

    careLineSubject: {
      marginTop: 0,
      marginBottom: theme.spacing(1),
    },

    actions: {},

    action: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),

      '&:first-of-type': {
        marginRight: theme.spacing(),
      },

      '& span': {
        display: 'flex',
        alignItems: 'center',
        fontSize: theme.utils.getSize(14),
      },

      '& svg': {
        marginRight: theme.spacing(1),
      },
    },
  }),
  { name: 'Review' },
)

export default Review
